import {readQuerySafe} from 'graphql-client/ApolloWrappers';
import {
  ParentStorableMinimalCapacityQuery,
  ParentStorableMinimalCapacityQueryVariables,
} from 'inventory/location-validation/__generated/ParentStorableMinimalCapacityQuery';
import {
  areItemsValidForParentStorable,
  getCapacityErrorMessage,
  isValidParentStorableWithCapacityInfo,
  PARENT_STORABLE_MINIMAL_CAPACITY_QUERY,
} from 'inventory/location-validation/ParentStorableValidationUtils';
import {BoxCreationStructuredTable} from 'notebook/integrations/samples/BoxCreationStructuredTableAdapter';
import InventoryStructuredTableMode from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {PlateCreationStructuredTable} from 'notebook/integrations/samples/PlateCreationStructuredTableAdapter';
import {
  TransferStructuredTable,
  TransferStructuredTableDestinationType,
  TransferStructuredTableMetadata,
} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {
  CellErrorPosition,
  PrimarySample,
  PrimarySampleLookupType,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {getStorableInfoFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import APIUtil from 'util/APIUtil';

function validateParentStorableCapacityAndGetErrors(
  rowIndexByParentId: RowIndexByCellValue,
  parentStorableColumnIndex: number,
  childSchema: {id: string; name: string}
): Array<CellErrorPosition> {
  const parentIds = Object.keys(rowIndexByParentId);
  const data = readQuerySafe<ParentStorableMinimalCapacityQuery, ParentStorableMinimalCapacityQueryVariables>(
    {query: PARENT_STORABLE_MINIMAL_CAPACITY_QUERY},
    {variables: {storableIds: parentIds}, fetchPolicy: 'cache-only'}
  );
  if (!data) {
    return [];
  }
  const {storables} = data;
  const errorInfoByStorableId: {
    [parentId: string]: {errorMessage: string | null; availableCapacity: number | null};
  } = {};
  for (const storable of storables) {
    if (isValidParentStorableWithCapacityInfo(storable)) {
      const capacityErrorInfo = areItemsValidForParentStorable(
        storable,
        rowIndexByParentId[storable.id].map((_) => ({storableSchema: childSchema}))
      );
      errorInfoByStorableId[storable.id] = {
        errorMessage: getCapacityErrorMessage(storable.name, capacityErrorInfo),
        availableCapacity: capacityErrorInfo.errorDetails.availableCapacity,
      };
    }
  }

  const errors: Array<CellErrorPosition> = [];
  for (const [storableId, {errorMessage, availableCapacity}] of Object.entries(errorInfoByStorableId)) {
    if (errorMessage != null) {
      const rowIndices = rowIndexByParentId[storableId];
      // Add errors to all rows past the available capacity
      const rowIndicesOvercapacity = rowIndices.slice(availableCapacity ?? 0);
      for (const rowIdx of rowIndicesOvercapacity) {
        errors.push({
          rowIndex: rowIdx,
          columnIndex: parentStorableColumnIndex,
          error: {
            type: 'Value error',
            message: errorMessage,
          },
        });
      }
    }
  }
  return errors;
}

// Mapping from a cell value to the row in the table it comes from
interface RowIndexByCellValue {
  [value: string]: Array<number>;
}

function getErrorsForParentStorableCapacity({
  structuredTable,
  computedTable,
  parentStorablesByRowId,
  parentStorableColumnIndex,
}: {
  structuredTable: TransferStructuredTable | BoxCreationStructuredTable | PlateCreationStructuredTable;
  computedTable?: Array<Array<EvaluatedCell>> | null;
  parentStorablesByRowId: {[rowId: string]: string};
  parentStorableColumnIndex: number;
}): Array<CellErrorPosition> {
  const schema = structuredTable.schemas[0];
  const rowIndexByParentId: RowIndexByCellValue = {};
  for (const [rowIndex, row] of structuredTable.rows.entries()) {
    if (
      row.isFinalized ||
      // Do not validate if cell already has an error. This grants invalid schema errors (added from custom
      // eval) higher priority.
      (computedTable != null && computedTable[rowIndex][parentStorableColumnIndex]?.error != null)
    ) {
      continue;
    }
    const parentId = parentStorablesByRowId[row.rowId] ?? null;
    if (parentId == null) {
      // Parent not yet present
      continue;
    }

    const destinationType =
      structuredTable.kind === StructuredTableKind.TRANSFER
        ? (structuredTable.metadata as TransferStructuredTableMetadata).destinationType
        : 'location';
    const parentStorableIsExpectedType =
      destinationType === TransferStructuredTableDestinationType.BOX
        ? APIUtil.isBox(parentId)
        : destinationType === TransferStructuredTableDestinationType.MATRIX_PLATE
          ? APIUtil.isPlate(parentId)
          : APIUtil.isLocation(parentId);
    // Parent is of wrong type; should not validate
    if (!parentStorableIsExpectedType) {
      continue;
    }

    if (rowIndexByParentId[parentId]) {
      rowIndexByParentId[parentId].push(rowIndex);
    } else {
      rowIndexByParentId[parentId] = [rowIndex];
    }
  }
  return validateParentStorableCapacityAndGetErrors(rowIndexByParentId, parentStorableColumnIndex, schema);
}

/**
 * Check for insufficient capacity errors across unfinalized rows.
 *
 * Takes into account the same parent being used across multiple rows with possibly insufficient total capacity -
 * e.g. a location used 5 times but only has 3 available spots. Errors are only added to the rows exceeding
 * the available capacity.
 *
 * Note: cross row validation cannot currently be asynchronous, so it cannot perform queries. As such, it
 * reads data from the cache to get the total & occupied capacity. This works because this data is always
 * preloaded for the chips, which will always be rendered. The data is also queried via the row updaters, which
 * enables this to be updated using the refresh action and when the page loads.
 * If for some reason the data is unavailable, this validation will simply not run and any errors will be
 * caught via backend errors on submit (not optimal, but acceptable).
 *
 * @param structuredTable plate/box creation or transfer table to add errors for
 * @param parentStorablesByRowId Object mapping row id to parent storable id.
 * @param parentStorableColumnIndex Index of the parent storable column (to put errors on)
 *
 * @returns list of errors to be added
 */
export function getErrorsForInvalidParentStorableCapacityInNonFinalizedRows({
  structuredTable,
  computedTable,
  parentStorablesByRowId,
  parentStorableColumnIndex,
}: {
  structuredTable: TransferStructuredTable | BoxCreationStructuredTable | PlateCreationStructuredTable;
  computedTable?: Array<Array<EvaluatedCell>> | null;
  parentStorablesByRowId: {[rowId: string]: string};
  parentStorableColumnIndex: number;
}): Array<CellErrorPosition> {
  if (structuredTable.kind === StructuredTableKind.TRANSFER) {
    const metadata = structuredTable.metadata as TransferStructuredTableMetadata;
    if (metadata.mode === InventoryStructuredTableMode.FILL) {
      return [];
    }
  }

  return getErrorsForParentStorableCapacity({
    structuredTable,
    computedTable,
    parentStorablesByRowId,
    parentStorableColumnIndex,
  });
}

/**
 * Extract parent storable id from a specific row in the plate/box creation or transfer table.
 */
function getParentStorableIdIfPresent({
  structuredTable,
  computedTable,
  rowIndex,
  parentStorableColumnIndex,
}: {
  structuredTable: TransferStructuredTable | BoxCreationStructuredTable | PlateCreationStructuredTable;
  computedTable?: Array<Array<EvaluatedCell>> | null;
  rowIndex: number;
  parentStorableColumnIndex: number;
}): string | null {
  // No parent storable sample present in fill transfer tables
  if (structuredTable.kind === StructuredTableKind.TRANSFER) {
    const metadata = structuredTable.metadata as TransferStructuredTableMetadata;
    if (metadata.mode === InventoryStructuredTableMode.FILL) {
      return null;
    }
  }

  const cells = structuredTable.rows[rowIndex].cells;
  const parentStorableTypedValue =
    computedTable != null
      ? computedTable[rowIndex][parentStorableColumnIndex]?.value
      : cells[parentStorableColumnIndex]?.typedValue;
  const parentStorableInfo = parentStorableTypedValue
    ? getStorableInfoFromTypedValue(parentStorableTypedValue)
    : null;

  if (parentStorableInfo != null) {
    return parentStorableInfo.id;
  }
  return null;
}

/**
 * Parse out parent storables by row id & the parent storable column index for plate/box creation tables.
 *
 * Pulled out here to be shared between both grid tables. Transfer table is more complicated, and has its
 * own custom logic.
 * @param structuredTable plate/box creation or transfer table
 * @param computedTable computed table to potentially pull parent values from
 * @param overrideParentColumnIndex parent storable column index to use. Optional, intended for use in more
 * complex cases. If not provided, the single column with table PARENT_STORABLE is used.
 *
 * @returns Object with parentStorablesByRowId and the parentStorableColumnIndex
 */
export function getParentStorablesByRowId({
  structuredTable,
  computedTable,
  overrideParentColumnIndex,
}: {
  structuredTable: BoxCreationStructuredTable | PlateCreationStructuredTable | TransferStructuredTable;
  computedTable?: Array<Array<EvaluatedCell>>;
  overrideParentColumnIndex?: number;
}): {parentStorablesByRowId: {[rowId: string]: string}; parentStorableColumnIndex: number} {
  const parentStorablesByRowId: {[rowId: string]: string} = {};
  // By default, just find the parent storable column. Allow overriding this logic for use with the transfer
  // table, which also validates plate + box parents.
  const parentStorableColumnIndex =
    overrideParentColumnIndex != null
      ? overrideParentColumnIndex
      : structuredTable.columns.findIndex((col) => col.columnType === TableColumnType.PARENT_STORABLE);

  for (const [rowIndex, {rowId, isFinalized}] of structuredTable.rows.entries()) {
    if (isFinalized) {
      continue;
    }
    const parentStorableId = getParentStorableIdIfPresent({
      structuredTable,
      computedTable,
      rowIndex,
      parentStorableColumnIndex,
    });
    if (parentStorableId != null) {
      parentStorablesByRowId[rowId] = parentStorableId;
    }
  }
  return {parentStorablesByRowId, parentStorableColumnIndex};
}

/**
 * Parse out parent storable primary sample for a specific row if unfinalized.
 *
 * Used to pull out parent primary sample in unfinalized rows for getPrimarySamples on the various tables,
 * which powers updating the capacity queries via the row updater refresh logic.
 *
 * @param structuredTable plate/box creation or transfer table
 * @param computedTable computed table to potentially pull parent values from
 * @param rowIndex row to pull parent from.
 * @param overrideParentColumnIndex parent storable column index to use. Optional, intended for use in more
 * complex cases. If not provided, the single column with table PARENT_STORABLE is used.
 *
 * @returns list of parent storable primary samples (singleton); empty if no parent or in finalized row.
 */
export function getUnfinalizedParentStorablePrimarySample({
  structuredTable,
  computedTable,
  rowIndex,
  overrideParentColumnIndex,
}: {
  structuredTable: BoxCreationStructuredTable | PlateCreationStructuredTable | TransferStructuredTable;
  computedTable?: Array<Array<EvaluatedCell>> | null;
  rowIndex: number;
  overrideParentColumnIndex?: number;
}): Array<PrimarySample> {
  const primarySamples: Array<PrimarySample> = [];
  const schema = structuredTable.schemas[0];
  if (schema == null || structuredTable.rows[rowIndex].isFinalized) {
    return [];
  }
  // By default, just find the parent storable column. Allow overriding this logic for use with the transfer
  // table, which also validates plate + box parents.
  const parentStorableColumnIndex =
    overrideParentColumnIndex != null
      ? overrideParentColumnIndex
      : structuredTable.columns.findIndex((col) => col.columnType === TableColumnType.PARENT_STORABLE);
  const parentStorableId = getParentStorableIdIfPresent({
    structuredTable,
    computedTable,
    rowIndex,
    parentStorableColumnIndex,
  });
  if (parentStorableId != null) {
    primarySamples.push({
      schemaId: schema.id,
      columnGroup:
        structuredTable.kind === StructuredTableKind.TRANSFER ? TransferTableColumnGroup.TARGET : null,
      sampleLookupInfo: {
        sampleType: PrimarySampleLookupType.DIRECT,
        idForLookup: parentStorableId,
        isUnfinalizedParentStorable: true,
      },
    });
  }
  return primarySamples;
}
